import React, { useState, useEffect, useMemo } from 'react';
import {
  useTaskQueue,
  useFetchAllTasksForPatient,
  useCompleteTask,
  useCompleteLabReview,
  useUpdateTaskRole,
  useUpdateTaskPriority,
  useUpdateTaskNote,
  useCreateTask,
  useEscalateTask,
  useResumeTask,
} from '../state/data/task';
import { useCreatePatientNote } from '../state/data/patient';
import { useCurrentUser } from '../state/data/account';
import { useFetchActiveCareperiodId } from '../state/data/carePeriod';
import { useNavigate } from 'react-router';
import { currentUpdaterRole, getProgramId } from '../config/program';
import { ELATION_PATIENT_BASE_URL } from '../config/medical-constants';
import CmsEligibilityModal from './CmsEligibilityModal';
import CmsReportingModal from './CmsReportingModal';
import TaskForm from './task/TaskForm';
import {
  ASSIGNABLE_ROLES, OWNER_ROLE_LABELS, CUSTOM_TASK_TEMPLATE_CODE, mintManualDedupeKey, dueDateToIso,
} from './task/constants';
import { ALIGNMENT_BLOCKING_ONB_CODES, REPORTING_KIND_BY_TEMPLATE, templateCodeOf } from './patient-file/shared/constants';
import { notifySpecFor } from '../logic/taskNotify';
import CompleteNotifyModal from './CompleteNotifyModal';
import SnoozeTaskModal from './SnoozeTaskModal';
import CareUpdateStatus from './CareUpdateStatus';
import LabResultsStatus from './LabResultsStatus';
import SpruceInboundStatus from './SpruceInboundStatus';
import ClaimEvidenceStatus from './ClaimEvidenceStatus';
import TaskIntakeFlags from './TaskIntakeFlags';
import WithdrawalRequestStatus from './WithdrawalRequestStatus';
import { isWithdrawalRequest } from '@/logic/withdrawalRequest';
import { completionActorOf, labNoteActorOf } from '@/logic/task';
import { labNoteOutcomeOf } from '@/logic/labs';
import {
  taskSnooze, isSnoozable, isSystemCompletedTask, isTaskOverdue, appointmentPresentation,
} from '@/logic/task';

const PRIORITY_ORDER = { urgent: 4, high: 3, normal: 2, low: 1 };

const PRIORITY_CONFIG = {
  urgent: { dot: 'var(--wi-status-bad)',       bg: 'var(--wi-background-bad)',      color: 'var(--wi-text-primary-bad)',      label: 'Urgent' },
  high:   { dot: '#F59E0B',                    bg: 'var(--wi-background-moderate)', color: 'var(--wi-text-primary-moderate)', label: 'High' },
  normal: { dot: 'var(--wi-status-undefined)', bg: 'var(--wi-background-tertiary)', color: 'var(--wi-text-secondary)',        label: 'Normal' },
  low:    { dot: 'var(--wi-status-good)',      bg: 'var(--wi-background-good)',     color: 'var(--wi-text-primary-good)',     label: 'Low' },
};


const DOMAIN_COLORS = {
  bp: '#1F4AC2', weight: '#059669', lab: '#7C3AED',
  medication: '#D97706', reporting: '#0284C7',
  compliance: '#BE185D', outreach: '#6B7280', device: '#374151',
};

/* ── Task row ─────────────────────────────────────────────────── */
function TaskRow({ task, isSelected, onClick, onPatientClick, hidePatient }) {
  const p = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
  const rowAppt = appointmentPresentation(task);
  const overdue = isTaskOverdue(task);

  return (
    <div
      onClick={onClick}
      className="table-cell-component"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 20px',
        cursor: 'pointer',
        background: isSelected
          ? 'var(--wi-status-info-10)'
          : overdue
            ? 'var(--wi-background-bad)'
            : 'transparent',
        borderLeft: `3px solid ${isSelected ? 'var(--wi-action-primary)' : 'transparent'}`,
        transition: 'background 150ms',
      }}
    >
      <div style={{ marginTop: 5, width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: p.dot }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <span className="body2" style={{ fontWeight: 500 }}>{task.title}</span>

          <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 4, background: p.bg, color: p.color }}>
            {p.label}
          </span>

          {task.clinical_domain && (
            <span style={{
              fontSize: 11, fontWeight: 500, padding: '1px 7px', borderRadius: 4,
              background: `${DOMAIN_COLORS[task.clinical_domain] || '#64748B'}18`,
              color: DOMAIN_COLORS[task.clinical_domain] || '#64748B',
            }}>
              {task.clinical_domain}
            </span>
          )}

          {task.owner_role && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 4,
              background: 'var(--wi-background-primary)', color: 'var(--wi-text-secondary)',
              border: '1px solid var(--wi-divider)', textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {OWNER_ROLE_LABELS[task.owner_role] || task.owner_role}
            </span>
          )}
        </div>

        {!hidePatient && (
          <button
            className="detail1 text-secondary"
            onClick={e => { e.stopPropagation(); onPatientClick && onPatientClick(task.withings_user_id); }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}
          >
            {task.patient_name || `ID ${task.withings_user_id}`}
            {task.patient_name && ` (${task.withings_user_id})`}
          </button>
        )}
      </div>

      {task.due_at && (
        <div className="detail1" style={{
          textAlign: 'right', flexShrink: 0,
          color: rowAppt?.label ? rowAppt.tone : (overdue ? 'var(--wi-status-bad)' : 'var(--wi-text-secondary)'),
          fontWeight: rowAppt?.label || overdue ? 500 : 400,
        }}>
          {rowAppt?.label
            || (overdue ? 'Overdue' : `Due ${new Date(task.due_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`)}
        </div>
      )}
    </div>
  );
}

/* ── Task detail drawer ───────────────────────────────────────── */
export function TaskDetailDrawer({ task, onComplete, onNoteChange: _onNoteChange, onRoleChange, onClose, onPatientClick, onTaskCreated, onOpenCarePlan, actor }) {
  const updateTaskPriorityMut = useUpdateTaskPriority();
  const navigate = useNavigate();
  const createTaskMut = useCreateTask();
  const escalateMut = useEscalateTask();
  const resumeMut = useResumeTask();
  const [snoozeTarget, setSnoozeTarget] = useState(null);
  const fetchActiveCareperiodId = useFetchActiveCareperiodId();
  const fetchAllTasksForPatient = useFetchAllTasksForPatient();
  const [note, setNote] = useState(task.completion_notes || '');
  const [noteError, setNoteError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState(null);
  const [roleUpdating, setRoleUpdating] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const [reviewRequesting, setReviewRequesting] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [escalating, setEscalating] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [escalateError, setEscalateError] = useState(null);
  const [escalateReason, setEscalateReason] = useState('');
  const [showEscalateInput, setShowEscalateInput] = useState(false);

  // onb_cms_alignment states
  const [onbTasks, setOnbTasks] = useState(null); // null = loading
  const [onbError, setOnbError] = useState(null);
  const [makingUrgent, setMakingUrgent] = useState(false);
  const [urgentDone, setUrgentDone] = useState(false);
  const [showCmsModal, setShowCmsModal] = useState(false);
  const [showReportingModal, setShowReportingModal] = useState(false);

  const templateCode = task.template?.template_code || task.context_json?.template_code || '';
  const isAlignmentTask = templateCode === 'onb_cms_alignment';
  const isEligibilityTask = templateCode === 'onb_eligibility_check';
  const reportingKind = REPORTING_KIND_BY_TEMPLATE[templateCode] || null;
  const isReportingTask = reportingKind !== null;
  const showMedReview = templateCode.startsWith('exc_bp') && !(task.owner_role === 'clinician' && task.clinical_domain === 'medication');
  const showCarePlanAction =
    templateCode === 'onb_care_plan_create' ||
    templateCode === 'onb_care_plan_md_review' ||
    templateCode === 'onb_care_plan_share';
  // Clinician-owned templates: action verb only when the viewer is a clinician
  // (they can actually edit). Coordinators viewing via the "all roles" lane see
  // a read-only "View care plan" label so the button label matches their ability.
  const viewerIsClinician = currentUpdaterRole() === 'clinician';
  const carePlanActionLabel =
    templateCode === 'onb_care_plan_create'    ? (viewerIsClinician ? 'Create care plan' : 'View care plan') :
    templateCode === 'onb_care_plan_md_review' ? (viewerIsClinician ? 'Review care plan' : 'View care plan') :
                                                 'Open care plan';
  const showOpenInElation = templateCode === 'onb_clinical_exclusion_screen' && !!task.ehr_patient_id;
  // WF17 refuses every change to a withdrawal request; only the Withdraw window cancels it (through WF67).
  const locked = isWithdrawalRequest(task);
  // Escalate-to-clinician: visible on any staff-owned task that isn't already clinician-owned.
  // Hidden for patient-owned and system tasks (those don't belong to the care team to escalate).
  const showEscalateToClinician = task.assigned_to_type === 'staff' && task.owner_role !== 'clinician' && !escalated && !locked;

  // Fetch all patient tasks when alignment drawer opens
  useEffect(() => {
    if (!isAlignmentTask) return;
    fetchAllTasksForPatient(task.withings_user_id)
      .then(all => {
        const onb = (all || []).filter(t =>
          ALIGNMENT_BLOCKING_ONB_CODES.has(templateCodeOf(t)) &&
          !['system', 'patient'].includes(t.owner_role)
        );
        setOnbTasks(onb);
      })
      .catch(e => setOnbError(e.message));
  }, [isAlignmentTask, task.withings_user_id, fetchAllTasksForPatient]);

  const pendingOnbTasks = (onbTasks || []).filter(t => ['pending', 'in_progress', 'overdue'].includes(t.status));
  const allOnbDone = onbTasks !== null && pendingOnbTasks.length === 0;

  async function handleMakeUrgent() {
    setMakingUrgent(true);
    try {
      await Promise.all(pendingOnbTasks.map(t => updateTaskPriorityMut.mutateAsync({ taskId: t.task_id, priority: 'urgent', withingsUserId: t.withings_user_id })));
      setOnbTasks(prev => prev.map(t =>
        ['pending', 'in_progress', 'overdue'].includes(t.status) ? { ...t, priority: 'urgent' } : t
      ));
      setUrgentDone(true);
    } catch (e) {
      setOnbError(e.message);
    } finally {
      setMakingUrgent(false);
    }
  }

  function handleAlign() {
    // Alignment runs in AlignmentPacketModal (full FHIR payload + signed attestation),
    // not a thin one-click. Open the patient file with the align intent; PatientFileShell
    // opens the packet (and re-runs its blocking-onboarding-task gate).
    navigate(`/team/${getProgramId()}/patient/${task.withings_user_id}?align=1`);
    onClose?.();
  }

  async function handleRequestMedReview() {
    setReviewRequesting(true);
    setReviewError(null);
    try {
      const careperiodId = await fetchActiveCareperiodId(String(task.withings_user_id));
      if (!careperiodId) throw new Error('No active care period found');
      const created = await createTaskMut.mutateAsync({
        withings_user_id: String(task.withings_user_id),
        care_period_id: careperiodId,
        owner_role: 'clinician',
        task_type: 'review',
        title: `Review Asked: ${task.title}`,
        description: task.description || '',
        clinical_domain: 'medication',
        priority: task.priority || 'normal',
        due_at: task.due_at || null,
      });
      onTaskCreated?.(created);
      setReviewDone(true);
    } catch (e) {
      setReviewError(e.message);
    } finally {
      setReviewRequesting(false);
    }
  }

  async function handleEscalateToClinician() {
    setEscalating(true);
    setEscalateError(null);
    try {
      await escalateMut.mutateAsync({ taskId: task.task_id, reason: escalateReason.trim(), withingsUserId: task.withings_user_id });
      setEscalated(true);
      setShowEscalateInput(false);
      // Mirror the role change via the parent's onRoleChange so the queue updates
      // its filtered view immediately. The parent's handler re-fires task_updated;
      // that second call is idempotent (same owner_role='clinician' value) and harmless.
      onRoleChange?.(task.task_id, 'clinician');
    } catch (e) {
      setEscalateError(e.message);
    } finally {
      setEscalating(false);
    }
  }
  const p = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
  const overdue = isTaskOverdue(task);

  async function handleComplete() {
    setCompleting(true);
    setCompleteError(null);
    try {
      // Falsy = the parent deferred to the pre-send confirm gate instead of
      // completing; release the spinner so a dismissed modal doesn't wedge it.
      const completed = await onComplete(task.task_id, note);
      if (!completed) setCompleting(false);
    } catch (e) {
      setCompleteError(e.message);
      setCompleting(false);
    }
  }

  async function handleRoleChange(e) {
    const newRole = e.target.value;
    if (newRole === task.owner_role) return;
    setRoleUpdating(true);
    setRoleError(null);
    try {
      await onRoleChange(task.task_id, newRole);
    } catch (err) {
      setRoleError(err.message);
    } finally {
      setRoleUpdating(false);
    }
  }

  const selectStyle = {
    padding: '4px 8px', borderRadius: 6,
    border: '1px solid var(--wi-divider)',
    background: 'var(--wi-background-secondary)',
    color: 'var(--wi-text-primary)', fontSize: 13,
    cursor: 'pointer', fontFamily: 'var(--wi-font-family)', outline: 'none',
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 190, background: 'rgba(0,0,0,0.12)' }} />

      <div style={{
        position: 'fixed', right: 0, top: 0, bottom: 0, width: 420,
        background: 'var(--wi-background-secondary)',
        borderLeft: '1px solid var(--wi-divider)',
        display: 'flex', flexDirection: 'column',
        zIndex: 200, boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
      }}>

        {/* Header */}
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid var(--wi-divider)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <p className="eyebrow text-secondary" style={{ margin: '0 0 6px' }}>Task detail</p>
            <h3 className="header4" style={{ margin: 0, lineHeight: 1.3 }}>{task.title}</h3>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--wi-text-secondary)', lineHeight: 1, padding: '2px 4px', flexShrink: 0 }}
            aria-label="Close"
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Patient */}
          <div style={{ background: 'var(--wi-background-primary)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <p className="eyebrow text-secondary" style={{ margin: '0 0 6px' }}>Patient</p>
              <p className="body2" style={{ margin: '0 0 2px', fontWeight: 500 }}>
                {task.patient_name || `ID ${task.withings_user_id}`}
              </p>
              <p className="detail1 text-secondary" style={{ margin: 0 }}>
                ID {task.withings_user_id}
              </p>
            </div>
            {onPatientClick && (
              <button
                className="btn-secondary"
                style={{ fontSize: 12, padding: '6px 12px', flexShrink: 0 }}
                onClick={() => onPatientClick(task.withings_user_id)}
              >
                View 360
              </button>
            )}
          </div>

          {/* Task metadata */}
          <div>
            <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Details</p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 4, background: p.bg, color: p.color }}>
                {p.label}
              </span>
              {task.clinical_domain && (
                <span style={{
                  fontSize: 11, fontWeight: 500, padding: '2px 9px', borderRadius: 4,
                  background: `${DOMAIN_COLORS[task.clinical_domain] || '#64748B'}18`,
                  color: DOMAIN_COLORS[task.clinical_domain] || '#64748B',
                }}>
                  {task.clinical_domain}
                </span>
              )}
            </div>

            {task.context_json?.template_code === 'onb_care_plan_share' && (
              <div style={{
                background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8,
                padding: '10px 14px', marginBottom: 12,
              }}>
                <span className="body2" style={{ fontWeight: 600, color: '#92400E' }}>ACTION TO DO IN ELATION</span>
              </div>
            )}

            {task.description && (
              <p className="body2" style={{ margin: '0 0 16px', lineHeight: 1.5, color: 'var(--wi-text-secondary)' }}>
                {task.description}
              </p>
            )}

            <TaskIntakeFlags task={task} style={{ margin: '0 0 16px' }} />
            <WithdrawalRequestStatus task={task} style={{ margin: '0 0 16px' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Assigned role — editable */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="detail1 text-secondary">Assigned to</span>
                {locked ? (
                  <span className="detail1">{OWNER_ROLE_LABELS[task.owner_role] || task.owner_role || '—'}</span>
                ) : (
                  <select
                    style={{ ...selectStyle, opacity: roleUpdating ? 0.6 : 1 }}
                    value={task.owner_role || ''}
                    onChange={handleRoleChange}
                    disabled={roleUpdating}
                  >
                    {ASSIGNABLE_ROLES.map(r => (
                      <option key={r} value={r}>{OWNER_ROLE_LABELS[r] || r}</option>
                    ))}
                  </select>
                )}
              </div>
              {roleError && (
                <span className="detail1" style={{ color: 'var(--wi-status-bad)', textAlign: 'right' }}>{roleError}</span>
              )}

              {task.due_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="detail1 text-secondary">Due</span>
                  <span className="detail1" style={{ color: overdue ? 'var(--wi-status-bad)' : 'var(--wi-text-primary)', fontWeight: overdue ? 500 : 400 }}>
                    {overdue ? 'Overdue · ' : ''}
                    {new Date(task.due_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}

              {(task.created ?? task.created_at) && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="detail1 text-secondary">Created</span>
                  <span className="detail1 text-secondary">
                    {new Date(task.created ?? task.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Alignment task: onboarding status block */}
          {isAlignmentTask && (
            <div>
              <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Onboarding status</p>
              {onbTasks === null && !onbError && (
                <p className="detail1 text-secondary">Checking onboarding tasks…</p>
              )}
              {onbError && (
                <p className="detail1" style={{ color: 'var(--wi-status-bad)' }}>{onbError}</p>
              )}
              {onbTasks !== null && (
                <>
                  {!allOnbDone ? (
                    <div style={{ background: 'var(--wi-background-bad)', border: '1px solid var(--wi-status-bad)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
                      <p className="body2" style={{ margin: '0 0 6px', fontWeight: 700, color: 'var(--wi-text-primary-bad)', fontSize: 15 }}>
                        Cannot align — {pendingOnbTasks.length} onboarding task{pendingOnbTasks.length !== 1 ? 's' : ''} still pending
                      </p>
                      <p className="detail1" style={{ margin: 0, color: 'var(--wi-text-primary-bad)' }}>
                        Complete all onboarding tasks before marking this task complete or triggering CMS alignment.
                      </p>
                      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                        {pendingOnbTasks.map(t => (
                          <li key={t.task_id} className="detail1" style={{ color: 'var(--wi-text-primary-bad)', marginBottom: 2 }}>
                            {t.title} <span style={{ opacity: 0.7 }}>({t.priority})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--wi-background-good)', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
                      <span style={{ fontSize: 20 }}>✅</span>
                      <span className="body2" style={{ fontWeight: 600, color: 'var(--wi-text-primary-good)' }}>Alignment ready</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Auto-pushed Care Update status (WF70) — read-only, review happens in Elation */}
          <CareUpdateStatus task={task} ehrPatientId={task.ehr_patient_id} />

          {/* Lab results landed in the Elation chart (WF72) — read-only */}
          <LabResultsStatus task={task} ehrPatientId={task.ehr_patient_id} />

          {/* A text, missed call or voicemail on the clinic line (WF84) */}
          <SpruceInboundStatus task={task} />

          {/* What a monthly G-code claim rests on (WF10) — the coordinator checks it before billing */}
          <ClaimEvidenceStatus task={task} />

          {/* Quick actions */}
          {(showMedReview || isAlignmentTask || isEligibilityTask || isReportingTask || showCarePlanAction || showEscalateToClinician || escalated) && (
            <div>
              <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Quick actions</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {isEligibilityTask && (
                  <button
                    className="btn-primary"
                    style={{ fontSize: 13 }}
                    onClick={() => setShowCmsModal(true)}
                  >
                    Run CMS Eligibility Check
                  </button>
                )}
                {isReportingTask && (
                  <button
                    className="btn-primary"
                    style={{ fontSize: 13 }}
                    onClick={() => setShowReportingModal(true)}
                  >
                    Run CMS Reporting Submission
                  </button>
                )}
                {!locked && (() => {
                  const snz = taskSnooze(task);
                  if (snz) {
                    return (
                      <button
                        className="btn-secondary"
                        style={{ fontSize: 13, alignSelf: 'flex-start' }}
                        title={snz.reason ? `Snoozed: ${snz.reason}` : 'Snoozed'}
                        disabled={resumeMut.isPending}
                        onClick={() => resumeMut.mutate({ taskId: task.task_id, withingsUserId: task.withings_user_id, actor })}
                      >
                        {`Snoozed until ${snz.until} — bring back`}
                      </button>
                    );
                  }
                  // exc_* clinical alerts are never snoozable — no button, by design.
                  if (!isSnoozable(task)) return null;
                  return (
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 13, alignSelf: 'flex-start' }}
                      onClick={() => setSnoozeTarget(task)}
                    >
                      Snooze
                    </button>
                  );
                })()}
                {(showEscalateToClinician || escalated) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {!showEscalateInput && !escalated && (
                      <button
                        className="btn-secondary"
                        style={{ fontSize: 13, alignSelf: 'flex-start' }}
                        onClick={() => setShowEscalateInput(true)}
                        disabled={escalating}
                      >
                        Escalate to Clinician
                      </button>
                    )}
                    {showEscalateInput && !escalated && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <textarea
                          value={escalateReason}
                          onChange={e => setEscalateReason(e.target.value)}
                          placeholder="Optional: brief clinical context for the clinician (e.g. BP 195/110 x3, no response to current ACEi)"
                          rows={2}
                          style={{ fontSize: 13, padding: 6, borderRadius: 4, border: '1px solid var(--wi-divider)', resize: 'vertical' }}
                          disabled={escalating}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn-primary"
                            style={{ fontSize: 13 }}
                            onClick={handleEscalateToClinician}
                            disabled={escalating}
                          >
                            {escalating ? 'Escalating…' : 'Confirm escalation'}
                          </button>
                          <button
                            className="btn-secondary"
                            style={{ fontSize: 13 }}
                            onClick={() => { setShowEscalateInput(false); setEscalateReason(''); }}
                            disabled={escalating}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {escalated && (
                      <span className="detail1" style={{ color: 'var(--wi-status-good)' }}>✓ Escalated to clinician — task now appears on the clinician roster.</span>
                    )}
                    {escalateError && <span className="detail1" style={{ color: 'var(--wi-status-bad)' }}>{escalateError}</span>}
                  </div>
                )}
                {showMedReview && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 13 }}
                      onClick={handleRequestMedReview}
                      disabled={reviewRequesting || reviewDone}
                    >
                      {reviewDone ? '✓ Review requested' : reviewRequesting ? 'Requesting…' : 'Ask for medication review'}
                    </button>
                    {reviewError && <span className="detail1" style={{ color: 'var(--wi-status-bad)' }}>{reviewError}</span>}
                  </div>
                )}
                {showCarePlanAction && (
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => onOpenCarePlan?.(task.withings_user_id)}
                  >
                    {carePlanActionLabel}
                  </button>
                )}
                {showOpenInElation && (
                  <a
                    className="btn-secondary"
                    href={`${ELATION_PATIENT_BASE_URL}/${task.ehr_patient_id}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open patient file in Elation (ID ${task.ehr_patient_id})`}
                    style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', alignSelf: 'flex-start' }}
                  >
                    Open patient file in Elation
                    <span aria-hidden="true" style={{ fontSize: 11, opacity: 0.8 }}>↗</span>
                  </a>
                )}
                {isAlignmentTask && onbTasks !== null && !allOnbDone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 13 }}
                      onClick={handleMakeUrgent}
                      disabled={makingUrgent || urgentDone || pendingOnbTasks.length === 0}
                    >
                      {urgentDone ? '✓ All marked urgent' : makingUrgent ? 'Updating…' : 'Make other onboarding tasks urgent'}
                    </button>
                  </div>
                )}
                {isAlignmentTask && allOnbDone && (
                  <button
                    className="btn-primary"
                    style={{ fontSize: 13 }}
                    onClick={handleAlign}
                  >
                    Review &amp; submit CMS alignment
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Note */}
          {!locked && (
            <div>
              <p className="eyebrow text-secondary" style={{ margin: '0 0 8px' }}>Completion note</p>
              <textarea
                className="call-notes-input"
                rows={3}
                placeholder="Add a note… (saved automatically on completion)"
                value={note}
                onChange={e => { setNote(e.target.value); setNoteError(null); }}
                style={{ width: '100%', resize: 'vertical', minHeight: 72 }}
              />
              {noteError && <span className="detail1" style={{ color: 'var(--wi-status-bad)', display: 'block', marginTop: 4 }}>{noteError}</span>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--wi-divider)' }}>
          {completeError && (
            <p className="detail1" style={{ color: 'var(--wi-status-bad)', margin: '0 0 10px' }}>{completeError}</p>
          )}
          {/* A CMS submission task closes itself when CMS accepts the report. Offering a
              hand-completion here would let someone assert a filing that never happened — and for
              the baseline, freeze WF9's rolling BP baseline and reveal it to the patient as
              official (S-23/S-30). Cancel stays available: deciding not to file is a human call. */}
          {locked || isSystemCompletedTask(task?.template_code) ? (
            <p className="detail1" style={{ margin: 0, color: 'var(--wi-text-secondary)', textAlign: 'center' }}>
              {locked
                ? <>Closes when the withdrawal is done. To cancel it, use <strong>Withdraw patient</strong> in the patient file.</>
                : <>Closes automatically once CMS accepts the submission — use <strong>Submit to CMS</strong> above.</>}
            </p>
          ) : (
            <button
              className="btn-primary"
              style={{ width: '100%', opacity: (isAlignmentTask && !allOnbDone) ? 0.4 : 1 }}
              onClick={handleComplete}
              disabled={completing || (isAlignmentTask && !allOnbDone)}
              title={isAlignmentTask && !allOnbDone ? 'Complete all onboarding tasks first' : undefined}
            >
              {completing ? 'Saving…' : 'Mark complete'}
            </button>
          )}
        </div>
      </div>
      {showCmsModal && (
        <CmsEligibilityModal
          patient={{ userid: task.withings_user_id }}
          carePeriodId={task.care_period_id}
          taskId={task.task_id}
          onClose={() => setShowCmsModal(false)}
        />
      )}
      {showReportingModal && (
        <CmsReportingModal
          patient={{ userid: task.withings_user_id }}
          carePeriodId={task.care_period_id}
          taskId={task.task_id}
          reportKind={reportingKind}
          onClose={() => { setShowReportingModal(false); onTaskCreated?.(); }}
        />
      )}
      {snoozeTarget && (
        <SnoozeTaskModal
          task={snoozeTarget}
          withingsUserId={snoozeTarget.withings_user_id}
          actor={actor}
          onClose={() => setSnoozeTarget(null)}
        />
      )}
    </>
  );
}

/* ── Create task drawer ────────────────────────────────────────── */
function CreateTaskDrawer({ allPatients, roleFilter, onCreated, onClose }) {
  const fetchActiveCareperiodId = useFetchActiveCareperiodId();
  const createTaskMut = useCreateTask();
  const [draft, setDraft] = useState({
    withings_user_id: '',
    title: '',
    description: '',
    priority: 'normal',
    clinical_domain: '',
    owner_role: roleFilter || 'care_coordinator',
    task_type: 'action',
    due_at: '',
  });
  const [careperiodId, setCareperiodId] = useState(null);
  const [careperiodLoading, setCareperiodLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dedupeKey] = useState(mintManualDedupeKey);

  function update(field, value) { setDraft(prev => ({ ...prev, [field]: value })); }

  async function handlePatientChange(e) {
    const userId = e.target.value;
    update('withings_user_id', userId);
    setCareperiodId(null);
    if (!userId) return;
    setCareperiodLoading(true);
    try {
      const id = await fetchActiveCareperiodId(String(userId));
      setCareperiodId(id);
      if (!id) setError('No active care period found for this patient');
      else setError(null);
    } catch {
      setError('Could not fetch care period for this patient');
    } finally {
      setCareperiodLoading(false);
    }
  }

  async function handleCreate() {
    if (!draft.title.trim()) { setError('Title is required'); return; }
    if (!draft.withings_user_id) { setError('Patient is required'); return; }
    if (!careperiodId) { setError('No active care period found for this patient'); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await createTaskMut.mutateAsync({
        ...draft,
        withings_user_id: String(draft.withings_user_id),
        care_period_id: careperiodId,
        due_at: dueDateToIso(draft.due_at),
        template_code: CUSTOM_TASK_TEMPLATE_CODE,
        dedupe_key: dedupeKey,
      });
      onCreated(created);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  const selectStyle = {
    padding: '4px 8px', borderRadius: 6,
    border: '1px solid var(--wi-divider)',
    background: 'var(--wi-background-secondary)',
    color: 'var(--wi-text-primary)', fontSize: 13,
    cursor: 'pointer', fontFamily: 'var(--wi-font-family)', outline: 'none',
    width: '100%',
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 190, background: 'rgba(0,0,0,0.12)' }} />

      <div style={{
        position: 'fixed', right: 0, top: 0, bottom: 0, width: 420,
        background: 'var(--wi-background-secondary)',
        borderLeft: '1px solid var(--wi-divider)',
        display: 'flex', flexDirection: 'column',
        zIndex: 200, boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
      }}>

        {/* Header */}
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid var(--wi-divider)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <p className="eyebrow text-secondary" style={{ margin: '0 0 6px' }}>New task</p>
            <h3 className="header4" style={{ margin: 0, lineHeight: 1.3 }}>Create a manual task</h3>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--wi-text-secondary)', lineHeight: 1, padding: '2px 4px', flexShrink: 0 }}
            aria-label="Close"
          >&times;</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Patient */}
          <div>
            <p className="eyebrow text-secondary" style={{ margin: '0 0 8px' }}>Patient</p>
            <select style={selectStyle} value={draft.withings_user_id} onChange={handlePatientChange}>
              <option value="">Select a patient…</option>
              {allPatients.map(p => (
                <option key={p.userid} value={p.userid}>
                  {[p.firstname, p.lastname].filter(Boolean).join(' ')} ({p.userid})
                </option>
              ))}
            </select>
            {careperiodLoading && (
              <p className="detail1 text-secondary" style={{ margin: '4px 0 0' }}>Fetching care period…</p>
            )}
          </div>

          <TaskForm draft={draft} onChange={update} />
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--wi-divider)' }}>
          {error && (
            <p className="detail1" style={{ color: 'var(--wi-status-bad)', margin: '0 0 10px' }}>{error}</p>
          )}
          <button
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={handleCreate}
            disabled={saving}
          >
            {saving ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ── Patient-grouped row (expandable) ─────────────────────────── */
function PatientGroupRow({ group, expandedId, onToggle, selectedTaskId, onTaskClick, onPatientClick, isFirst }) {
  const isExpanded = expandedId === group.patientId;
  const p = PRIORITY_CONFIG[group.topPriority] || PRIORITY_CONFIG.normal;
  const typeLabels = [...group.taskTypes].sort().join(', ');

  return (
    <div>
      {/* Patient header row */}
      <div
        onClick={() => onToggle(group.patientId)}
        className="table-cell-component"
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 20px', cursor: 'pointer',
          background: isExpanded ? 'var(--wi-background-primary)' : 'transparent',
          borderLeft: `3px solid ${isExpanded ? 'var(--wi-action-primary)' : 'transparent'}`,
          marginTop: isFirst ? 0 : 12,
          borderTop: isFirst ? 'none' : '2px solid var(--wi-divider)',
          transition: 'background 150ms',
        }}
      >
        {/* Priority dot */}
        <div style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: p.dot }} />

        {/* Patient name + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span className="body2" style={{ fontWeight: 600 }}>{group.patientName}</span>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 4, background: p.bg, color: p.color }}>
              {p.label}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={e => { e.stopPropagation(); onPatientClick && onPatientClick(group.patientId); }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--wi-action-primary)', textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              360 view
            </button>
            <span className="detail1 text-secondary">·</span>
            <span className="detail1 text-secondary">{group.taskCount} task{group.taskCount !== 1 ? 's' : ''} · {typeLabels || 'misc'}</span>
          </div>
        </div>

        {/* Earliest due */}
        {group.earliestDue && (
          <span className="detail1" style={{
            flexShrink: 0, textAlign: 'right',
            color: group.hasOverdue ? 'var(--wi-status-bad)' : 'var(--wi-text-secondary)',
            fontWeight: group.hasOverdue ? 500 : 400,
          }}>
            {group.hasOverdue ? 'Overdue' : `Due ${new Date(group.earliestDue).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
          </span>
        )}

        {/* Chevron */}
        <span style={{ fontSize: 14, color: 'var(--wi-text-secondary)', transition: 'transform 150ms', transform: isExpanded ? 'rotate(90deg)' : 'none' }}>
          ›
        </span>
      </div>

      {/* Expanded task list */}
      {isExpanded && (
        <div style={{
          borderLeft: '3px solid var(--wi-action-primary)',
          background: 'var(--wi-background-secondary)',
          paddingBottom: 4,
        }}>
          {group.tasks.map(task => (
            <TaskRow
              key={task.task_id}
              task={task}
              isSelected={selectedTaskId === task.task_id}
              onClick={() => onTaskClick(task.task_id)}
              onPatientClick={onPatientClick}
              hidePatient
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main TaskQueue ────────────────────────────────────────────── */
export default function TaskQueue({ allPatients = [], onBadgeCountChange, roleFilter, initialRoleFilter, viewMode = 'task', currentUser: currentUserProp }) {
  // No mount passes currentUser; without the session user every completion here reads "by care team".
  const { data: session } = useCurrentUser();
  const currentUser = currentUserProp ?? session?.currentUser ?? null;
  const [selectedId, setSelectedId] = useState(null);
  const [notifyGate, setNotifyGate] = useState(null);
  const navigate = useNavigate();
  const completeTaskMut = useCompleteTask();
  const completeLabReviewMut = useCompleteLabReview();
  const createNoteMut = useCreatePatientNote();
  const updateTaskNoteMut = useUpdateTaskNote();
  const updateTaskRoleMut = useUpdateTaskRole();
  const [filterPriority, setFilterPriority] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterRole, setFilterRole] = useState(initialRoleFilter || '');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [expandedPatient, setExpandedPatient] = useState(null);

  // Sync role filter when navigated from dashboard
  useEffect(() => {
    if (initialRoleFilter != null) setFilterRole(initialRoleFilter);
  }, [initialRoleFilter]);

  // Build patient name lookup from allPatients (Withings data)
  const patientNames = useMemo(() => {
    const map = new Map();
    allPatients.forEach(p => {
      const name = [p.firstname, p.lastname].filter(Boolean).join(' ');
      if (name) map.set(String(p.userid), name);
    });
    return map;
  }, [allPatients]);

  // Elation EHR id lookup — sourced from the dashboard-view merge in App.tsx.
  const ehrPatientIds = useMemo(() => {
    const map = new Map();
    allPatients.forEach(p => {
      if (p.ehr_patient_id) map.set(String(p.userid), p.ehr_patient_id);
    });
    return map;
  }, [allPatients]);

  function handlePatientClick(withingsUserId) {
    navigate(`/team/${getProgramId()}/patient/${withingsUserId}`);
  }

  function handleOpenCarePlan(withingsUserId) {
    navigate(`/team/${getProgramId()}/patient/${withingsUserId}?tab=care-plan`);
  }

  // Array roleFilters fetch all + filter client-side; string passes to the server.
  const queueRole = Array.isArray(roleFilter) ? null : (roleFilter ?? null);
  const queueQuery = useTaskQueue(queueRole);
  const tasks = useMemo(() => {
    const rows = queueQuery.data;
    if (!rows) return [];
    if (!roleFilter) return rows;
    return rows.filter(t => (Array.isArray(roleFilter)
      ? roleFilter.includes(t.owner_role)
      : t.owner_role === roleFilter));
  }, [queueQuery.data, roleFilter]);
  const loading = queueQuery.isLoading;
  const error = queueQuery.error?.message ?? null;

  // Keep sidebar badge in sync (only for the main Tasks tab)
  useEffect(() => {
    if (!onBadgeCountChange) return;
    const count = tasks.filter(t =>
      ['urgent', 'high'].includes(t.priority) && ['pending', 'in_progress'].includes(t.status)
    ).length;
    onBadgeCountChange(count);
  }, [tasks, onBadgeCountChange]);

  const selectedTask = tasks.find(t => t.task_id === selectedId) || null;

  const filtered = useMemo(() => {
    return tasks
      .filter(t => {
        if (filterStatus === 'overdue') {
          if (!isTaskOverdue(t)) return false;
        } else if (filterStatus === 'completed') {
          if (t.status !== 'completed') return false;
        } else {
          if (!['pending', 'in_progress'].includes(t.status)) return false;
        }
        if (filterPriority && t.priority !== filterPriority) return false;
        if (filterDomain && t.clinical_domain !== filterDomain) return false;
        if (!roleFilter && filterRole) {
          if (filterRole === '_unassigned') {
            if (t.owner_role) return false;
          } else if (t.owner_role !== filterRole) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        const pd = (PRIORITY_ORDER[b.priority] || 0) - (PRIORITY_ORDER[a.priority] || 0);
        if (pd !== 0) return pd;
        return new Date(a.due_at || 0) - new Date(b.due_at || 0);
      })
      .map(t => ({ ...t, patient_name: patientNames.get(String(t.withings_user_id)) || null }));
  }, [tasks, filterPriority, filterDomain, filterRole, filterStatus, patientNames, roleFilter]);

  // ── Patient-grouped view ──────────────────────────────────────
  const patientGroups = useMemo(() => {
    if (viewMode !== 'patient') return [];
    const map = new Map();
    filtered.forEach(t => {
      const pid = String(t.withings_user_id);
      if (!map.has(pid)) {
        map.set(pid, {
          patientId: pid,
          patientName: t.patient_name || `ID ${pid}`,
          tasks: [],
          taskTypes: new Set(),
          topPriorityOrder: 0,
          topPriority: 'low',
          earliestDue: null,
          hasOverdue: false,
        });
      }
      const g = map.get(pid);
      g.tasks.push(t);
      if (t.task_type) g.taskTypes.add(t.task_type);
      const po = PRIORITY_ORDER[t.priority] || 0;
      if (po > g.topPriorityOrder) { g.topPriorityOrder = po; g.topPriority = t.priority; }
      if (t.due_at) {
        const d = new Date(t.due_at);
        if (!g.earliestDue || d < new Date(g.earliestDue)) g.earliestDue = t.due_at;
        if (isTaskOverdue(t)) g.hasOverdue = true;
      }
    });
    return [...map.values()]
      .map(g => ({ ...g, taskCount: g.tasks.length }))
      .sort((a, b) => {
        const pd = b.topPriorityOrder - a.topPriorityOrder;
        if (pd !== 0) return pd;
        if (!a.earliestDue && !b.earliestDue) return 0;
        if (!a.earliestDue) return 1;
        if (!b.earliestDue) return -1;
        return new Date(a.earliestDue) - new Date(b.earliestDue);
      });
  }, [filtered, viewMode]);

  // Completing some tasks makes WF17 text + email the patient irreversibly.
  // Gate those; everything else stays one-click. Returns true once the task is
  // actually completed, false when the confirm gate took over.
  async function handleComplete(taskId, note) {
    const task = tasks.find(t => t.task_id === taskId);
    if (task && notifySpecFor(templateCodeOf(task))) {
      setNotifyGate({ task, note, busy: false });
      return false;
    }
    return performComplete(taskId, note);
  }

  async function handleNotifyConfirm(labNote) {
    if (!notifyGate || notifyGate.busy) return;
    const { task, note } = notifyGate;
    setNotifyGate(g => (g ? { ...g, busy: true, error: null, noteOutcome: null } : g));
    // With the care team's note: the result says whether the completion and the note landed.
    if (labNote) {
      let r;
      try {
        r = await completeLabReviewMut.mutateAsync({
          taskId: task.task_id, withingsUserId: task.withings_user_id, labNote, notes: note, actor: labNoteActorOf(currentUser, currentUpdaterRole()),
        });
      } catch (e) {
        // Refused before anything was sent (the note failed the module's check).
        setNotifyGate(g => (g ? { ...g, busy: false, error: e.message } : g));
        return;
      }
      if (r.completed) { logCompletionToProfile(task, note); setSelectedId(null); }
      const noteOutcome = labNoteOutcomeOf(r);
      if (!noteOutcome) { setNotifyGate(null); return; }
      setNotifyGate(g => (g ? { ...g, busy: false, noteOutcome, noteError: r.sendError ?? null } : g));
      return;
    }
    try {
      await performComplete(task.task_id, note);
      setNotifyGate(null);
    } catch (e) {
      // Keep the gate open with the failure. Closing silently here would look
      // exactly like success on the one action the operator must not misread:
      // they'd believe the patient was texted.
      setNotifyGate(g => (g ? { ...g, busy: false, error: e.message } : g));
    }
  }

  // The internal note only: the patient's lab note is never copied into the Withings profile note.
  function logCompletionToProfile(task, note) {
    if (!task?.withings_user_id) return;
    const parts = [`[Task completed — ${task.title}]`];
    if (note?.trim()) parts.push(note.trim());
    const authorId = currentUser?.id ?? currentUser?.userid ?? 0;
    createNoteMut.mutate({ userid: task.withings_user_id, message: parts.join(' '), authorid: authorId });
  }

  async function performComplete(taskId, note) {
    const task = tasks.find(t => t.task_id === taskId);
    await completeTaskMut.mutateAsync({
      taskId, notes: note, withingsUserId: task?.withings_user_id,
      actor: completionActorOf(currentUser, currentUpdaterRole()),
    });
    // Also log completion to the patient's Withings RPM profile (best-effort).
    logCompletionToProfile(task, note);

    // TanStack invalidates TASK_KEYS.all on mutation success → queueQuery refetches.
    setSelectedId(null);
    return true;
  }

  async function handleNoteChange(taskId, note) {
    const task = tasks.find(t => t.task_id === taskId);
    await updateTaskNoteMut.mutateAsync({ taskId, notes: note, withingsUserId: task?.withings_user_id });
    // Mutation invalidates; queueQuery refetches with fresh completion_notes.
  }

  async function handleRoleChange(taskId, newRole) {
    const task = tasks.find(t => t.task_id === taskId);
    await updateTaskRoleMut.mutateAsync({ taskId, role: newRole, withingsUserId: task?.withings_user_id });
    // Mutation invalidates TASK_KEYS.all → queueQuery refetches with the new role.
    // If the task moved off this filtered tab, close the drawer.
    if (roleFilter && !(Array.isArray(roleFilter) ? roleFilter.includes(newRole) : newRole === roleFilter)) {
      setSelectedId(null);
    }
  }

  function handleTaskCreated() {
    // Mutation invalidates TASK_KEYS.all → queueQuery picks up the new row.
    setShowCreate(false);
  }

  const urgentCount = tasks.filter(t => t.priority === 'urgent' && ['pending', 'in_progress'].includes(t.status)).length;
  const highCount   = tasks.filter(t => t.priority === 'high'   && ['pending', 'in_progress'].includes(t.status)).length;

  const domains = [...new Set(tasks.map(t => t.clinical_domain).filter(Boolean))].sort();
  const roles   = [...new Set(tasks.map(t => t.owner_role).filter(Boolean))].sort();
  const hasFilters = filterPriority || filterDomain || filterRole || filterStatus;

  const selectStyle = {
    padding: '6px 10px', borderRadius: 7,
    border: '1px solid var(--wi-divider)',
    background: 'var(--wi-background-secondary)',
    color: 'var(--wi-text-primary)', fontSize: 13,
    cursor: 'pointer', fontFamily: 'var(--wi-font-family)', outline: 'none',
  };

  if (loading) {
    return (
      <div style={{ padding: '24px 0' }}>
        <p className="body2 text-secondary">Loading tasks…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px 0' }}>
        <p className="body2" style={{ color: 'var(--wi-status-bad)' }}>Failed to load tasks: {error}</p>
      </div>
    );
  }

  // Enrich selectedTask with patient name + Elation id for drawer
  const selectedTaskEnriched = selectedTask
    ? {
        ...selectedTask,
        patient_name: patientNames.get(selectedTask.withings_user_id) || null,
        ehr_patient_id: ehrPatientIds.get(String(selectedTask.withings_user_id)) || null,
      }
    : null;

  return (
    <div style={{ marginBottom: 32 }}>

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {urgentCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--wi-background-bad)', borderRadius: 8, padding: '10px 16px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--wi-status-bad)' }} />
            <span className="body2" style={{ fontWeight: 500, color: 'var(--wi-text-primary-bad)' }}>
              {urgentCount} urgent task{urgentCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        {highCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--wi-background-moderate)', borderRadius: 8, padding: '10px 16px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B' }} />
            <span className="body2" style={{ fontWeight: 500, color: 'var(--wi-text-primary-moderate)' }}>
              {highCount} high priority task{highCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        {urgentCount === 0 && highCount === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--wi-background-good)', borderRadius: 8, padding: '10px 16px' }}>
            <span className="body2" style={{ fontWeight: 500, color: 'var(--wi-text-primary-good)' }}>
              ✓ No urgent or high priority tasks
            </span>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        marginBottom: 16, padding: '12px 16px',
        background: 'var(--wi-background-secondary)',
        borderRadius: 10, border: '1px solid var(--wi-divider)',
      }}>
        <select style={selectStyle} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="">All priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>

        <select style={selectStyle} value={filterDomain} onChange={e => setFilterDomain(e.target.value)}>
          <option value="">All domains</option>
          {domains.map(d => <option key={d} value={d}>{d}</option>)}
        </select>

        {/* Only show role filter when not pre-filtered by role */}
        {!roleFilter && (
          <select style={selectStyle} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="">All roles</option>
            {roles.map(r => <option key={r} value={r}>{OWNER_ROLE_LABELS[r] || r}</option>)}
            <option value="_unassigned">Unassigned</option>
          </select>
        )}

        <select style={selectStyle} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Active tasks</option>
          <option value="overdue">Overdue only</option>
          <option value="completed">Completed</option>
        </select>

        {hasFilters && (
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '6px 12px' }}
            onClick={() => { setFilterPriority(''); setFilterDomain(''); setFilterRole(''); setFilterStatus(''); }}
          >
            Clear filters
          </button>
        )}

        <span className="detail1 text-secondary" style={{ marginLeft: 'auto' }}>
          {viewMode === 'patient'
            ? `${patientGroups.length} patient${patientGroups.length !== 1 ? 's' : ''} · ${filtered.length} task${filtered.length !== 1 ? 's' : ''}`
            : `${filtered.length} task${filtered.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Table header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px' }}>
        <span className="eyebrow text-secondary">{viewMode === 'patient' ? 'Patient' : 'Task'}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="eyebrow text-secondary">Due</span>
          <button
            onClick={() => setShowCreate(true)}
            title="Create task"
            style={{
              width: 26, height: 26, borderRadius: '50%', border: '1px solid var(--wi-divider)',
              background: 'var(--wi-background-secondary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, lineHeight: 1, color: 'var(--wi-action-primary)', fontWeight: 500,
            }}
          >+</button>
        </div>
      </div>

      {/* Task / Patient list */}
      {filtered.length === 0 ? (
        <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '32px 20px', textAlign: 'center' }}>
          <p className="body2 text-secondary" style={{ margin: 0 }}>
            {tasks.length === 0 ? 'No active tasks.' : 'No tasks match the current filters.'}
          </p>
        </div>
      ) : viewMode === 'patient' ? (
        <div className="table-component">
          {patientGroups.map((group, idx) => (
            <PatientGroupRow
              key={group.patientId}
              group={group}
              isFirst={idx === 0}
              expandedId={expandedPatient}
              onToggle={pid => setExpandedPatient(expandedPatient === pid ? null : pid)}
              selectedTaskId={selectedId}
              onTaskClick={taskId => setSelectedId(selectedId === taskId ? null : taskId)}
              onPatientClick={handlePatientClick}
            />
          ))}
        </div>
      ) : (
        <div className="table-component">
          {filtered.map(task => (
            <TaskRow
              key={task.task_id}
              task={task}
              isSelected={selectedId === task.task_id}
              onClick={() => setSelectedId(selectedId === task.task_id ? null : task.task_id)}
              onPatientClick={handlePatientClick}
            />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {selectedTaskEnriched && (
        <TaskDetailDrawer
          key={selectedTaskEnriched.task_id}
          task={selectedTaskEnriched}
          onComplete={handleComplete}
          onNoteChange={handleNoteChange}
          onRoleChange={handleRoleChange}
          onClose={() => setSelectedId(null)}
          onPatientClick={handlePatientClick}
          onTaskCreated={handleTaskCreated}
          onOpenCarePlan={handleOpenCarePlan}
          actor={completionActorOf(currentUser, currentUpdaterRole())}
        />
      )}

      {/* Pre-send confirm — completing this task messages the patient (WF17) */}
      {notifyGate && (
        <CompleteNotifyModal
          key={notifyGate.task.task_id}
          task={notifyGate.task}
          withingsUserId={notifyGate.task.withings_user_id}
          busy={notifyGate.busy}
          error={notifyGate.error}
          noteOutcome={notifyGate.noteOutcome}
          noteError={notifyGate.noteError}
          author={labNoteActorOf(currentUser, currentUpdaterRole())}
          onConfirm={handleNotifyConfirm}
          onDismiss={() => setNotifyGate(null)}
        />
      )}

      {/* Create task drawer */}
      {showCreate && (
        <CreateTaskDrawer
          allPatients={allPatients}
          roleFilter={roleFilter}
          onCreated={handleTaskCreated}
          onClose={() => setShowCreate(false)}
        />
      )}

    </div>
  );
}
